// Browser mirror of the import-free Edge policy. Keep this file in lockstep
// with supabase/functions/_shared/pickup-reconciliation-policy.ts; the parity
// suite executes both implementations against the same fixtures.
export const PICKUP_LOCK_HASH_VERSION = 2;
export const PICKUP_PRICING_HASH_VERSION = 2;
export const PICKUP_OB_BUSINESS_TIME_ZONE = 'America/Chicago';

export type PickupVerificationStatus =
  | 'verified'
  | 'verified_with_drift'
  | 'pending_verification'
  | 'waiting_for_ob_sync'
  | 'ob_mismatch'
  | 'ob_access_error';

export type PickupMismatchTier = 'blocking' | 'advisory';

export const PICKUP_CANONICAL_ENCOMPASS_INPUT_COLUMNS = [
  'encompass_loan_id',
  'rate_lock_status',
  'rate_lock_buy_side_lock_date',
  'rate_lock_request_lock_date',
  'rate_lock_buy_side_lock_expires',
  'rate_lock_buy_side_number_of_days',
  'rate_lock_request_number_of_days',
  'rate_lock_buy_side_rate',
  'rate_lock_buy_side_adjustments',
  'rate_lock_total_buy_price',
  'rate_lock_loan_program',
  'rate_lock_plan_code',
  'investor_name',
  'rate_lock_investor_commitment',
  'rate_lock_base_loan_amount',
  'trans_details_loan_amt',
  'trans_details_loan_purpose',
  'trans_details_loan_type',
  'loan_term_months',
  'amortization_type',
  'lien_position',
  'cx_loan_level_fico',
  'borrower_middle_score',
  'coborrower_middle_score',
  'dti_ratio',
  'rate_lock_ltv',
  'freddie_mac_ltv_ratio_pct',
  'rate_lock_cltv',
  'trans_details_cltv_ratio_pct',
  'rate_lock_hcltv',
  'hcltv_ratio',
  'rate_lock_property_estimated_value',
  'property_value',
  'trans_details_purchase_price',
  'rate_lock_property_appraised_value',
  'subject_property_appraised_value',
  'property_type',
  'project_classification',
  'is_non_warrantable_project',
  'occupancy_status',
  'subject_property_state',
  'property_county',
  'property_zip',
  'property_units',
  'rate_lock_documentation_type',
  'cx_doc_type',
  'rate_lock_prepay_penalty',
  'rate_lock_prepay_term_months',
  'prepay_term_months',
  'rate_lock_impound_waived',
  'rate_lock_self_employed_indicator',
  'self_employed_indicator',
  'rate_lock_first_time_homebuyer',
  'rate_lock_lender_paid_mi',
  'rate_lock_mi_premium_percent',
  'rate_lock_funding_amount',
  'rate_lock_mip_paid_in_cash',
  'interest_only_term_months',
  'trans_details_interest_rate',
  'aus_type',
  'aus_recommendation',
  'aus_key',
  'dscr_ratio'
] as const;

export type PickupCanonicalEncompassInputColumn =
  (typeof PICKUP_CANONICAL_ENCOMPASS_INPUT_COLUMNS)[number];

export type PickupCanonicalLoanInput = Partial<
  Record<PickupCanonicalEncompassInputColumn, unknown>
>;

export function hasPickupCanonicalEncompassInputs(
  input: PickupCanonicalLoanInput
): boolean {
  return PICKUP_CANONICAL_ENCOMPASS_INPUT_COLUMNS.every((column) => (
    Object.prototype.hasOwnProperty.call(input, column)
  ));
}

export interface PickupCanonicalHashes {
  lockHash: string;
  pricingHash: string;
  lockDurationDays: number | null;
}

export interface PickupObComparisonInput {
  lockedRate?: unknown;
  lockedPrice?: unknown;
  investor?: unknown;
  product?: unknown;
  lockExpirationAt?: unknown;
  baseLoanAmount?: unknown;
  loanPurpose?: unknown;
  loanType?: unknown;
  loanTermMonths?: unknown;
  amortizationType?: unknown;
  lienPosition?: unknown;
  representativeFico?: unknown;
  dti?: unknown;
  ltv?: unknown;
  cltv?: unknown;
  hcltv?: unknown;
  secondLienSubjectRatio?: unknown;
  secondLienCombinedRatio?: unknown;
  estimatedValue?: unknown;
  appraisedValue?: unknown;
  propertyType?: unknown;
  projectClassification?: unknown;
  nonWarrantableProject?: unknown;
  occupancy?: unknown;
  propertyState?: unknown;
  propertyCounty?: unknown;
  propertyZip?: unknown;
  propertyUnits?: unknown;
  documentationType?: unknown;
  documentationScope?: 'expanded' | 'standard';
  documentationApiValue?: unknown;
  prepaymentPenalty?: unknown;
  impoundWaived?: unknown;
  selfEmployed?: unknown;
  firstTimeHomebuyer?: unknown;
  lenderPaidMi?: unknown;
  miPremiumPercent?: unknown;
  fundingAmount?: unknown;
  mipPaidInCash?: unknown;
  interestOnlyTermMonths?: unknown;
  ausType?: unknown;
  dscrRatio?: unknown;
  lockPeriodBucket?: unknown;
}

export interface PickupObCacheComparisonSource {
  raw_loan_payload?: unknown;
  raw_product_payload?: unknown;
  expanded_guidelines?: unknown;
  investor_name?: unknown;
  product_name?: unknown;
  locked_rate?: unknown;
  locked_price?: unknown;
  lock_period?: unknown;
  lock_expiration_date?: unknown;
  base_loan_amount?: unknown;
  loan_purpose?: unknown;
  loan_type?: unknown;
  lien_type?: unknown;
  representative_fico?: unknown;
  dti?: unknown;
  ltv?: unknown;
  cltv?: unknown;
  hcltv?: unknown;
  sales_price?: unknown;
  appraised_value?: unknown;
  property_type?: unknown;
  occupancy?: unknown;
  property_state?: unknown;
  property_county?: unknown;
  property_zip?: unknown;
  property_number_of_units?: unknown;
}

const BLOCKING_FIELDS = [
  'locked_rate',
  'locked_price',
  'investor',
  'product',
  'lock_expiration_date',
  'prepayment_penalty',
  'documentation_type',
  'note_rate',
  'occupancy',
  'aus_type',
  'aus_integrity'
] as const;

const DYNAMIC_TIER_FIELDS = [
  'dti'
] as const;

const ADVISORY_FIELDS = [
  'base_loan_amount',
  'loan_purpose',
  'loan_type',
  'loan_term_months',
  'amortization_type',
  'lien_position',
  'representative_fico',
  'fico_internal_consistency',
  'ltv',
  'cltv',
  'hcltv',
  'ltv_arithmetic',
  'estimated_value',
  'appraised_value',
  'property_type',
  'project_classification',
  'non_warrantable_project',
  'property_state',
  'property_county',
  'property_zip',
  'property_units',
  'prepayment_penalty_flag',
  'impound_waived',
  'self_employed',
  'first_time_homebuyer',
  'lender_paid_mi',
  'mi_premium_percent',
  'funding_amount',
  'mip_paid_in_cash',
  'interest_only_term_months',
  'dscr_ratio'
] as const;

export const PICKUP_BLOCKING_COMPARISON_FIELDS = [
  ...BLOCKING_FIELDS,
  ...DYNAMIC_TIER_FIELDS
];
export const PICKUP_ADVISORY_COMPARISON_FIELDS = [
  ...ADVISORY_FIELDS,
  ...DYNAMIC_TIER_FIELDS
];

export type PickupComparisonField =
  | (typeof BLOCKING_FIELDS)[number]
  | (typeof ADVISORY_FIELDS)[number]
  | (typeof DYNAMIC_TIER_FIELDS)[number];

export type PickupFieldVerdict =
  | 'match'
  | 'drift'
  | 'missing_in_ob'
  | 'normalized_match';

export interface PickupFieldComparison {
  field: PickupComparisonField;
  label: string;
  tier: PickupMismatchTier;
  encompassRaw: unknown;
  obRaw: unknown;
  verdict: PickupFieldVerdict;
  note?: string;
}

export type PickupComparisonView = 'all' | 'blocking' | 'minor';

export interface PickupVerificationPresentation {
  status: PickupVerificationStatus;
  blocking: PickupFieldComparison[];
  minor: PickupFieldComparison[];
  missingInOb: PickupFieldComparison[];
  defaultView: PickupComparisonView;
  consequence: string;
}

function pickupComparisonIsDifference(comparison: PickupFieldComparison): boolean {
  return comparison.verdict === 'drift' || comparison.verdict === 'missing_in_ob';
}

export function derivePickupVerificationPresentation(input: {
  comparisons: PickupFieldComparison[];
  currentStatus?: PickupVerificationStatus | null;
  attemptCount?: number | null;
  canonicalStateChanged?: boolean;
}): PickupVerificationPresentation {
  const blocking = input.comparisons.filter((comparison) => (
    comparison.tier === 'blocking' && pickupComparisonIsDifference(comparison)
  ));
  const minor = input.comparisons.filter((comparison) => (
    comparison.tier === 'advisory' && pickupComparisonIsDifference(comparison)
  ));
  const missingInOb = [...blocking, ...minor].filter((comparison) => (
    comparison.verdict === 'missing_in_ob'
  ));
  const attemptCount = Math.max(0, Math.min(2, Math.trunc(Number(input.attemptCount) || 0)));

  let status: PickupVerificationStatus;
  if (input.canonicalStateChanged) {
    status = 'pending_verification';
  } else if (input.currentStatus === 'ob_access_error') {
    status = 'ob_access_error';
  } else if (blocking.length > 0) {
    status = input.currentStatus === 'ob_mismatch' || attemptCount >= 2
      ? 'ob_mismatch'
      : 'waiting_for_ob_sync';
  } else {
    status = minor.length > 0 ? 'verified_with_drift' : 'verified';
  }

  let consequence: string;
  const blockingDriftCount = blocking.filter(({ verdict }) => verdict === 'drift').length;
  const blockingMissingCount = blocking.length - blockingDriftCount;
  if (status === 'ob_access_error') {
    consequence = 'Pricing waits — OB could not be reached for this loan.';
  } else if (status === 'pending_verification') {
    consequence = 'Pricing waits — this loan has not been checked against OB yet.';
  } else if (blockingDriftCount > 0) {
    consequence = `Pricing blocked — ${blocking.length} pricing-critical ${blocking.length === 1 ? 'field differs' : 'fields differ'} between Loan Center and Optimal Blue.`;
  } else if (blockingMissingCount > 0) {
    consequence = `Pricing waits — Optimal Blue has not returned ${blockingMissingCount} pricing-critical ${blockingMissingCount === 1 ? 'field' : 'fields'} yet.`;
  } else if (minor.length > 0) {
    consequence = `${minor.length} ${minor.length === 1 ? 'field differs' : 'fields differ'}, none blocking — pricing stays enabled.`;
  } else {
    consequence = 'All compared fields match — pricing stays enabled.';
  }

  return {
    status,
    blocking,
    minor,
    missingInOb,
    defaultView: blocking.length > 0 ? 'blocking' : minor.length > 0 ? 'minor' : 'all',
    consequence
  };
}

export interface PickupVerificationMismatch {
  field: string;
  tier: PickupMismatchTier;
}

export const PICKUP_COMPARISON_FIELD_LABELS: Record<PickupComparisonField, string> = {
  locked_rate: 'Locked rate',
  locked_price: 'Locked price',
  investor: 'Investor',
  product: 'Product',
  lock_expiration_date: 'Lock expiration',
  base_loan_amount: 'Base loan amount',
  loan_purpose: 'Loan purpose',
  loan_type: 'Loan type',
  loan_term_months: 'Loan term',
  amortization_type: 'Amortization',
  lien_position: 'Lien position',
  representative_fico: 'Representative FICO',
  fico_internal_consistency: 'Loan Center FICO consistency',
  dti: 'DTI',
  ltv: 'LTV',
  cltv: 'CLTV',
  hcltv: 'HCLTV',
  ltv_arithmetic: 'Loan Center LTV arithmetic',
  estimated_value: 'Estimated value',
  appraised_value: 'Appraised value',
  property_type: 'Property type',
  project_classification: 'Project classification',
  non_warrantable_project: 'Non-warrantable project',
  occupancy: 'Occupancy',
  property_state: 'Property state',
  property_county: 'Property county',
  property_zip: 'Property ZIP',
  property_units: 'Property units',
  documentation_type: 'Documentation type',
  prepayment_penalty: 'Prepayment penalty',
  prepayment_penalty_flag: 'Lock request prepayment flag',
  impound_waived: 'Impounds waived',
  self_employed: 'Self-employed',
  first_time_homebuyer: 'First-time homebuyer',
  lender_paid_mi: 'Lender-paid MI',
  mi_premium_percent: 'MI premium',
  funding_amount: 'Funding amount',
  mip_paid_in_cash: 'MIP paid in cash',
  interest_only_term_months: 'Interest-only term',
  note_rate: 'Note rate',
  aus_type: 'AUS type',
  aus_integrity: 'Loan Center AUS consistency',
  dscr_ratio: 'DSCR'
};

function firstPresent(...values: unknown[]): unknown {
  return values.find((value) => value !== null && value !== undefined && value !== '');
}

function firstDefined(...values: unknown[]): unknown {
  return values.find((value) => value !== undefined);
}

function textValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = String(value).trim().replace(/\s+/g, ' ');
  return normalized || null;
}

function keyValue(value: unknown): string | null {
  const normalized = textValue(value)
    ?.normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  return normalized || null;
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function derivedSecondLienCombinedRatio(
  loanInformation: unknown,
  propertyInformation: unknown
): number | undefined {
  const firstLienAmount = finiteNumber(recordValue(loanInformation, 'firstLienAmount'));
  const baseLoanAmount = finiteNumber(recordValue(loanInformation, 'baseLoanAmount'));
  const appraisedValue = finiteNumber(recordValue(propertyInformation, 'appraisedValue'));
  if (firstLienAmount === null
    || baseLoanAmount === null
    || appraisedValue === null
    || appraisedValue <= 0) {
    return undefined;
  }
  return Number((((firstLienAmount + baseLoanAmount) / appraisedValue) * 100).toFixed(3));
}

function derivedSecondLienSubjectRatio(
  baseLoanAmount: unknown,
  appraisedValue: unknown
): number | undefined {
  const amount = finiteNumber(baseLoanAmount);
  const value = finiteNumber(appraisedValue);
  if (amount === null || value === null || value <= 0) {
    return undefined;
  }
  return Number(((amount / value) * 100).toFixed(3));
}

function fixedNumber(value: unknown, places: number): string | null {
  const parsed = finiteNumber(value);
  return parsed === null ? null : parsed.toFixed(places);
}

// Encompass 2160 (the buy-side net buy rate): the raw rate_lock_buy_side_rate,
// untouched, unless a nonzero buy-side `BaseRate` adjustment exists. Inline copy
// of `lockRateValue` in supabase/functions/_shared/lock-net-buy-rate.ts because
// this file must stay import-free; a cross-check test keeps the copies equal.
function pickupRateMicro(value: unknown): number | null | 'out_of_domain' {
  let text: string;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return 'out_of_domain';
    }
    text = String(value);
    const exponent = /^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/.exec(text);
    if (exponent) {
      const [, sign, intPart, fracPart = '', exponentText] = exponent;
      const digits = intPart + fracPart;
      const pointIndex = intPart.length + Number(exponentText);
      text = pointIndex <= 0
        ? `${sign}0.${'0'.repeat(-pointIndex)}${digits}`
        : pointIndex >= digits.length
          ? `${sign}${digits}${'0'.repeat(pointIndex - digits.length)}`
          : `${sign}${digits.slice(0, pointIndex)}.${digits.slice(pointIndex)}`;
    }
  } else if (typeof value === 'string') {
    text = value.replace(/[,$%\s]/g, '');
    if (!/^-?([0-9]+(\.[0-9]+)?|\.[0-9]+)$/.test(text)) {
      return null;
    }
  } else {
    return null;
  }
  const negative = text.startsWith('-');
  const [intPart, fracPart = ''] = (negative ? text.slice(1) : text).split('.');
  const intDigits = intPart.replace(/^0+/, '');
  if (intDigits.length > 6) {
    return 'out_of_domain';
  }
  const fraction = `${fracPart}0000000`.slice(0, 7);
  let micro = Number(intDigits || '0') * 1_000_000 + Number(fraction.slice(0, 6));
  if (Number(fraction[6]) >= 5) {
    micro += 1;
  }
  return micro === 0 ? 0 : negative ? -micro : micro;
}

function pickupLockRateValue(input: PickupCanonicalLoanInput): unknown {
  const raw = input.rate_lock_buy_side_rate;
  const base = pickupRateMicro(raw);
  if (base === null || base === 'out_of_domain') {
    return raw;
  }
  let magnitude = Math.abs(base);
  if (magnitude >= 1_000_000_000_000) {
    return raw;
  }
  let adjustment = 0;
  const lines = input.rate_lock_buy_side_adjustments;
  if (Array.isArray(lines)) {
    for (const line of lines) {
      if (typeof line !== 'object'
        || line === null
        || (line as { priceAdjustmentType?: unknown }).priceAdjustmentType !== 'BaseRate') {
        continue;
      }
      const micro = pickupRateMicro((line as { rate?: unknown }).rate);
      if (micro === null) {
        continue;
      }
      if (micro === 'out_of_domain') {
        return raw;
      }
      magnitude += Math.abs(micro);
      if (magnitude >= 1_000_000_000_000) {
        return raw;
      }
      adjustment += micro;
    }
  }
  return adjustment === 0 ? raw : (base + adjustment) / 1_000_000;
}

function integerValue(value: unknown): number | null {
  const parsed = finiteNumber(value);
  return parsed !== null && Number.isInteger(parsed) ? parsed : null;
}

function booleanValue(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  const key = keyValue(value);
  if (['true', 'yes', 'y', 'waived', '1'].includes(key || '')) {
    return true;
  }
  if (['false', 'no', 'n', 'notwaived', '0'].includes(key || '')) {
    return false;
  }
  return null;
}

function invertedBooleanValue(value: unknown): boolean | undefined {
  const parsed = booleanValue(value);
  return parsed === null ? undefined : !parsed;
}

function dateOnly(value: unknown): string | null {
  const text = textValue(value);
  if (!text) {
    return null;
  }
  const match = text.match(/^(\d{4}-\d{2}-\d{2})(?:$|T|\s)/);
  if (match && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(text)) {
    return match[1];
  }
  const time = Date.parse(text);
  return Number.isFinite(time) ? new Date(time).toISOString().slice(0, 10) : null;
}

export function pickupObBusinessDate(value: unknown): string | null {
  const text = textValue(value);
  if (!text) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }
  const time = Date.parse(text);
  if (!Number.isFinite(time)) {
    return null;
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PICKUP_OB_BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date(time));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function parseInvestorFromProgram(value: unknown): string | null {
  const program = textValue(value);
  if (!program) {
    return null;
  }
  const channelSeparatorIndex = program.indexOf(' - ');
  if (channelSeparatorIndex > 0) {
    return textValue(program.slice(0, channelSeparatorIndex));
  }
  const legacyIndex = program.indexOf('(');
  return legacyIndex > 0 ? textValue(program.slice(0, legacyIndex)) : null;
}

export function normalizePickupReconciliationInvestor(value: unknown): string | null {
  const normalized = (parseInvestorFromProgram(value) ?? textValue(value))
    ?.toLowerCase()
    .replace(/\b(llc|inc|corp|corporation|lending|mortgage|capital|group|partners|financial|servicing|na|fsb)\b\.?/g, '')
    .replace(/[^a-z0-9]+/g, '');
  return normalized || null;
}

function productFromProgram(value: unknown): string | null {
  const program = textValue(value);
  if (!program) {
    return null;
  }
  const channelQualified = program.match(
    /^.+? - (?:Correspondent|Wholesale|Non-Delegated) - (.+)$/i
  );
  if (channelQualified) {
    return textValue(channelQualified[1]);
  }
  const legacyMatch = program.match(/^.+?\((?:Correspondent|Wholesale|Non-Delegated)\)\s*(.+)$/i);
  return textValue(legacyMatch?.[1] ?? program);
}

export function normalizePickupReconciliationProduct(value: unknown): string | null {
  return keyValue(productFromProgram(value));
}

function recordValue(value: unknown, key: string): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return (value as Record<string, unknown>)[key];
}

function selectedObProductName(productData: unknown): string | null {
  return textValue(recordValue(productData, 'name'))
    || textValue(recordValue(productData, 'investorProductName'))
    || textValue(recordValue(productData, 'productName'));
}

function selectedObInvestorName(
  productData: unknown,
  cacheInvestor: unknown
): string | null {
  return textValue(recordValue(productData, 'investor'))
    || textValue(cacheInvestor)
    || parseInvestorFromProgram(recordValue(productData, 'name'))
    || parseInvestorFromProgram(recordValue(productData, 'investorProductName'))
    || parseInvestorFromProgram(recordValue(productData, 'productName'))
    || null;
}

function hasExpandedDocumentationScope(
  productTypes: unknown,
  selectedProduct: unknown
): boolean {
  const types = Array.isArray(productTypes) ? productTypes : [productTypes];
  if (types.some((value) => keyValue(value) === 'expandedguidelines')) {
    return true;
  }
  return /\s-\sEG\s*$/i.test(textValue(selectedProduct) || '');
}

export function buildPickupObComparisonInput(
  cacheRow: PickupObCacheComparisonSource
): PickupObComparisonInput {
  const loan = recordValue(cacheRow, 'raw_loan_payload');
  const loanInfo = recordValue(loan, 'loanInformation');
  const property = recordValue(loan, 'propertyInformation');
  const borrower = recordValue(loan, 'borrowerInformation');
  const totalLoanAmountDetails = recordValue(loanInfo, 'totalLoanAmountDetails');
  const expanded = firstPresent(
    recordValue(cacheRow, 'expanded_guidelines'),
    recordValue(loanInfo, 'expandedGuidelines')
  );
  const productData = recordValue(cacheRow, 'raw_product_payload');
  const pricingDetail = recordValue(productData, 'pricingDetail');
  const selectedProduct = selectedObProductName(productData)
    || textValue(recordValue(cacheRow, 'product_name'))
    || 'Unknown';
  const selectedInvestor = selectedObInvestorName(
    productData,
    recordValue(cacheRow, 'investor_name')
  )
    || parseInvestorFromProgram(selectedProduct)
    || 'Unknown';
  const lienPosition = firstPresent(
    recordValue(cacheRow, 'lien_type'),
    recordValue(loanInfo, 'lienType')
  );
  const expandedDocumentationType = recordValue(expanded, 'incomeVerificationType');
  const documentationScope = hasExpandedDocumentationScope(
    recordValue(loanInfo, 'productTypes'),
    selectedProduct
  ) ? 'expanded' : 'standard';
  const baseLoanAmount = firstPresent(
    recordValue(cacheRow, 'base_loan_amount'),
    recordValue(loanInfo, 'baseLoanAmount')
  );
  const appraisedValue = firstPresent(
    recordValue(cacheRow, 'appraised_value'),
    recordValue(property, 'appraisedValue')
  );

  return {
    lockedRate: firstPresent(recordValue(pricingDetail, 'rate'), recordValue(cacheRow, 'locked_rate')),
    lockedPrice: firstPresent(recordValue(pricingDetail, 'price'), recordValue(cacheRow, 'locked_price')),
    investor: selectedInvestor,
    product: selectedProduct,
    lockExpirationAt: recordValue(cacheRow, 'lock_expiration_date'),
    baseLoanAmount,
    loanPurpose: firstPresent(recordValue(cacheRow, 'loan_purpose'), recordValue(loanInfo, 'loanPurpose')),
    loanType: firstPresent(recordValue(cacheRow, 'loan_type'), recordValue(loanInfo, 'loanType')),
    loanTermMonths: firstPresent(
      recordValue(loanInfo, 'loanTermMonths'),
      recordValue(loanInfo, 'loanTerm'),
      recordValue(loanInfo, 'loanTerms')
    ),
    amortizationType: firstPresent(recordValue(loanInfo, 'amortizationTypes'), recordValue(loanInfo, 'amortizationType')),
    lienPosition,
    representativeFico: firstPresent(
      recordValue(cacheRow, 'representative_fico'),
      recordValue(loanInfo, 'representativeFICO'),
      recordValue(borrower, 'fico')
    ),
    dti: firstPresent(recordValue(cacheRow, 'dti'), recordValue(loanInfo, 'loanLevelDebtToIncomeRatio')),
    ltv: firstPresent(recordValue(cacheRow, 'ltv'), recordValue(loan, 'ltv')),
    cltv: firstPresent(recordValue(cacheRow, 'cltv'), recordValue(loan, 'cltv')),
    hcltv: firstPresent(recordValue(cacheRow, 'hcltv'), recordValue(loan, 'hcltv')),
    secondLienSubjectRatio: derivedSecondLienSubjectRatio(baseLoanAmount, appraisedValue),
    secondLienCombinedRatio: derivedSecondLienCombinedRatio(loanInfo, property),
    estimatedValue: firstPresent(recordValue(cacheRow, 'sales_price'), recordValue(property, 'salesPrice')),
    appraisedValue,
    propertyType: firstPresent(recordValue(cacheRow, 'property_type'), recordValue(property, 'propertyType')),
    projectClassification: undefined,
    nonWarrantableProject: undefined,
    occupancy: firstPresent(recordValue(cacheRow, 'occupancy'), recordValue(property, 'occupancy')),
    propertyState: firstPresent(recordValue(cacheRow, 'property_state'), recordValue(property, 'state')),
    propertyCounty: firstPresent(recordValue(cacheRow, 'property_county'), recordValue(property, 'county')),
    propertyZip: firstPresent(recordValue(cacheRow, 'property_zip'), recordValue(property, 'zipCode')),
    propertyUnits: firstPresent(recordValue(cacheRow, 'property_number_of_units'), recordValue(property, 'numberOfUnits')),
    documentationType: documentationScope === 'expanded'
      ? expandedDocumentationType
      : 'Full Doc (Standard product)',
    documentationScope,
    documentationApiValue: expandedDocumentationType,
    prepaymentPenalty: recordValue(loanInfo, 'prepaymentPenalty'),
    impoundWaived: recordValue(borrower, 'waiveEscrows'),
    selfEmployed: recordValue(borrower, 'selfEmployed'),
    firstTimeHomebuyer: recordValue(borrower, 'firstTimeHomeBuyer'),
    lenderPaidMi: invertedBooleanValue(recordValue(loanInfo, 'borrowerPaidMI')),
    miPremiumPercent: recordValue(totalLoanAmountDetails, 'upfrontPMI_MIP_FF_GF_Percent'),
    fundingAmount: recordValue(totalLoanAmountDetails, 'upfrontPMI_MIP_FF_GF_Amount'),
    mipPaidInCash: recordValue(totalLoanAmountDetails, 'upfrontPMI_MIP_FF_GF_PaidinCash'),
    interestOnlyTermMonths: recordValue(loanInfo, 'interestOnly'),
    ausType: recordValue(loanInfo, 'automatedUnderwritingSystem'),
    dscrRatio: recordValue(expanded, 'debtServiceCoverageRatio'),
    lockPeriodBucket: firstPresent(recordValue(cacheRow, 'lock_period'), recordValue(pricingDetail, 'lockPeriod'))
  };
}

function canonicalPurpose(value: unknown): string | null {
  const key = keyValue(value);
  const aliases: Record<string, string> = {
    cashoutrefinance: 'reficashout',
    reficashout: 'reficashout',
    nocashoutrefinance: 'refiratetermlimitedco',
    refiratetermlimitedco: 'refiratetermlimitedco',
    purchase: 'purchase',
    fhastreamlinerefi: 'fhastreamlinerefi',
    varatereduction: 'varatereduction',
    simplerefinance: 'simplerefinance'
  };
  return key ? aliases[key] ?? key : null;
}

function canonicalLoanType(value: unknown): string | null {
  const key = keyValue(value);
  return key;
}

function loanTypesEqual(expected: string | null, actual: string | null): boolean {
  if (expected === 'conventional') {
    return ['conventional', 'conforming', 'nonconforming'].includes(actual || '');
  }
  return expected === actual;
}

function canonicalLien(value: unknown): string | null {
  const key = keyValue(value);
  if (key === 'firstlien' || key === 'first') {
return 'first';
}
  if (key === 'secondlien' || key === 'second') {
return 'second';
}
  return key;
}

function canonicalAmortization(value: unknown): string | null {
  const first = Array.isArray(value) ? value[0] : value;
  const key = keyValue(first);
  if (key === 'adjustablerate' || key === 'arm') {
return 'arm';
}
  return key;
}

function canonicalOccupancy(value: unknown): string | null {
  const key = keyValue(value);
  if (key === 'investor' || key === 'investmentproperty') {
return 'investmentproperty';
}
  return key;
}

function canonicalPropertyType(value: unknown, nonWarrantable?: unknown): string | null {
  const key = keyValue(value);
  if (key === 'detached' || key === 'singlefamily') {
return 'singlefamily';
}
  if (key === 'highrisecondominium') {
return 'nonwarrantablecondo';
}
  if (key === 'condominium' || key === 'condo') {
    return booleanValue(nonWarrantable) === true ? 'nonwarrantablecondo' : 'condo';
  }
  return key;
}

function canonicalState(value: unknown): string | null {
  const text = textValue(value);
  if (!text) {
return null;
}
  const parenthetical = text.match(/\(([A-Za-z]{2})\)\s*$/);
  if (parenthetical) {
return parenthetical[1].toLowerCase();
}
  return /^[A-Za-z]{2}$/.test(text) ? text.toLowerCase() : keyValue(text);
}

function canonicalZip(value: unknown): string | null {
  const digits = textValue(value)?.replace(/[^0-9]/g, '') || '';
  return digits ? digits.slice(0, 5) : null;
}

function canonicalUnits(value: unknown): number | null {
  const direct = integerValue(value);
  if (direct !== null) {
return direct;
}
  const aliases: Record<string, number> = {
    oneunit: 1,
    twounits: 2,
    threeunits: 3,
    fourunits: 4
  };
  return aliases[keyValue(value) || ''] ?? null;
}

function canonicalLoanTerm(value: unknown): number | null {
  const direct = integerValue(value);
  if (direct !== null) {
return direct;
}
  const first = Array.isArray(value) ? value[0] : value;
  const aliases: Record<string, number> = {
    fortyyear: 480,
    thirtyyear: 360,
    twentyfiveyear: 300,
    twentyyear: 240,
    fifteenyear: 180,
    twelveyear: 144,
    tenyear: 120,
    nineyear: 108,
    eightyear: 96,
    sevenyear: 84,
    sixyear: 72,
    fiveyear: 60,
    fouryear: 48,
    threeyear: 36,
    twoyear: 24,
    oneyear: 12,
    ninemonth: 9,
    sixmonth: 6,
    threemonth: 3
  };
  return aliases[keyValue(first) || ''] ?? null;
}

function canonicalAusType(value: unknown): string | null {
  const key = keyValue(value);
  if (key === 'manualtraditional' || key === 'manualunderwriting') {
    return 'manualunderwriting';
  }
  if (key === 'desktopunderwriter') {
    return 'du';
  }
  if (key === 'loanprospector' || key === 'loanproductadvisor') {
    return 'lp';
  }
  return key;
}

function canonicalAusRecommendation(value: unknown): string | null {
  const key = keyValue(value);
  if (key === 'accept') {
    return 'accept';
  }
  if (key === 'approveeligible' || key === 'approve') {
    return 'approveeligible';
  }
  return key;
}

function derivedAusType(
  recommendationValue: unknown,
  declaredAusValue: unknown
): string | null {
  const recommendation = canonicalAusRecommendation(recommendationValue);
  if (recommendation === 'accept') {
    return 'lp';
  }
  if (recommendation === 'approveeligible') {
    return 'du';
  }
  return canonicalAusType(declaredAusValue);
}

function canonicalDocumentationType(value: unknown): string | null {
  const key = keyValue(value);
  if (
    key === 'fulldocumentation'
    || key === 'fulldoc'
    || key === 'fulldoc1year'
    || key === 'fulldoc2year'
  ) {
    return 'fulldoc';
  }
  const aliases: Record<string, string> = {
    businessbankstatement12mos: 'businessbankstmt12mos',
    businessbankstatement12mo: 'businessbankstmt12mos',
    businessbankstatement24mos: 'businessbankstmt24mos',
    personalbankstatement12mos: 'personalbankstmt12mos',
    personalbankstatement24mos: 'personalbankstmt24mos',
    pl1year: 'ploneyear',
    pl2year: 'pltwoyear',
    '10991year': 'oneyear1099',
    '10992year': 'twoyear1099',
    assetutilization: 'assetrelated',
    assetdepletion: 'assetrelated',
    dscr: 'investordscr',
    dscrnoratio: 'investornoratio'
  };
  if (key && aliases[key]) {
    return aliases[key];
  }
  return key;
}

function isAlternativeDocumentationType(value: string | null): boolean {
  return value === 'assetrelated'
    || value === 'ploneyear'
    || value === 'pltwoyear'
    || value?.includes('bankstmt') === true;
}

function documentationTypesEqual(expectedValue: unknown, actualValue: unknown): boolean {
  const expected = canonicalDocumentationType(expectedValue);
  const actual = canonicalDocumentationType(actualValue);
  if (expected === null) {
    return true;
  }
  if (expected === 'alternative') {
    return isAlternativeDocumentationType(actual);
  }
  return expected === actual;
}

function prepaymentPenaltyPresent(value: unknown): boolean | null {
  const boolean = booleanValue(value);
  if (boolean !== null) {
    return boolean;
  }
  const key = keyValue(value);
  if (!key) {
    return null;
  }
  if (key === 'none' || key === 'no') {
    return false;
  }
  return true;
}

function canonicalPrepaymentTerm(value: unknown): string | null {
  const numeric = finiteNumber(value);
  if (numeric !== null && Number.isInteger(numeric)) {
    return String(numeric);
  }
  const key = keyValue(value);
  if (!key || key === 'none' || key === 'no') {
    return key ? '0' : null;
  }
  const aliases: Record<string, string> = {
    sixmonth: '6',
    sixmonths: '6',
    oneyear: '12',
    twoyear: '24',
    threeyear: '36',
    fouryear: '48',
    fiveyear: '60'
  };
  return aliases[key] ?? key;
}

function interestOnlyPresent(value: unknown): boolean | null {
  const boolean = booleanValue(value);
  if (boolean !== null) {
    return boolean;
  }
  const months = finiteNumber(value);
  return months === null ? null : months > 0;
}

function projectClassificationIsBenign(value: unknown): boolean {
  const key = keyValue(value);
  return key === null || key === 'gnotinaprojectordevelopment';
}

function nonWarrantableProjectIsBenign(value: unknown): boolean {
  return rawValueIsMissing(value) || booleanValue(value) === false;
}

function calculatedLoanCenterLtv(input: PickupCanonicalLoanInput): {
  reportedLtv: number;
  calculatedLtv: number;
  basisValue: number;
  basis: 'lesser_of_purchase_or_appraised' | 'appraised_value';
  difference: number;
} | null {
  const baseLoanAmount = finiteNumber(firstPresent(
    input.rate_lock_base_loan_amount,
    input.trans_details_loan_amt
  ));
  const reportedLtv = finiteNumber(firstPresent(
    input.freddie_mac_ltv_ratio_pct,
    input.rate_lock_ltv
  ));
  const purchasePrice = finiteNumber(input.trans_details_purchase_price);
  const appraisedValue = finiteNumber(firstPresent(
    input.subject_property_appraised_value,
    input.rate_lock_property_appraised_value
  ));
  const purchase = canonicalPurpose(input.trans_details_loan_purpose) === 'purchase';
  const purchaseBasisCandidates = [purchasePrice, appraisedValue]
    .filter((value): value is number => value !== null && value > 0);
  const basisValue = purchase
    ? (purchaseBasisCandidates.length > 0 ? Math.min(...purchaseBasisCandidates) : null)
    : appraisedValue;
  if (baseLoanAmount === null
    || baseLoanAmount <= 0
    || reportedLtv === null
    || basisValue === null
    || basisValue <= 0) {
    return null;
  }
  const calculatedLtv = Number(((baseLoanAmount / basisValue) * 100).toFixed(3));
  return {
    reportedLtv,
    calculatedLtv,
    basisValue,
    basis: purchase ? 'lesser_of_purchase_or_appraised' : 'appraised_value',
    difference: Math.abs(reportedLtv - calculatedLtv)
  };
}

export function canonicalPickupLockIdentity(input: PickupCanonicalLoanInput): string {
  const program = textValue(input.rate_lock_loan_program);
  return JSON.stringify({
    contractVersion: PICKUP_LOCK_HASH_VERSION,
    encompassLoanId: keyValue(input.encompass_loan_id),
    status: keyValue(input.rate_lock_status),
    lockDate: dateOnly(firstPresent(
      input.rate_lock_buy_side_lock_date,
      input.rate_lock_request_lock_date
    )),
    expirationDate: dateOnly(input.rate_lock_buy_side_lock_expires),
    durationDays: integerValue(firstPresent(
      input.rate_lock_buy_side_number_of_days,
      input.rate_lock_request_number_of_days
    )),
    rate: fixedNumber(pickupLockRateValue(input), 3),
    price: fixedNumber(input.rate_lock_total_buy_price, 3),
    investor: normalizePickupReconciliationInvestor(
      parseInvestorFromProgram(program) ?? input.investor_name
    ),
    product: normalizePickupReconciliationProduct(program),
    planCode: keyValue(input.rate_lock_plan_code),
    lockFormPrepayTermMonths: integerValue(input.rate_lock_prepay_term_months),
    loanLevelPrepayTermMonths: integerValue(input.prepay_term_months)
  });
}

export function canonicalPickupPricingInputs(input: PickupCanonicalLoanInput): string {
  return JSON.stringify({
    contractVersion: PICKUP_PRICING_HASH_VERSION,
    baseLoanAmount: fixedNumber(firstPresent(input.rate_lock_base_loan_amount, input.trans_details_loan_amt), 2),
    loanPurpose: canonicalPurpose(input.trans_details_loan_purpose),
    loanType: canonicalLoanType(input.trans_details_loan_type),
    loanTermMonths: canonicalLoanTerm(input.loan_term_months),
    amortizationType: canonicalAmortization(input.amortization_type),
    lienPosition: canonicalLien(input.lien_position),
    representativeFico: integerValue(input.cx_loan_level_fico),
    borrowerMiddleScore: integerValue(input.borrower_middle_score),
    dti: fixedNumber(input.dti_ratio, 3),
    ltv: fixedNumber(firstPresent(input.freddie_mac_ltv_ratio_pct, input.rate_lock_ltv), 3),
    cltv: fixedNumber(firstPresent(input.trans_details_cltv_ratio_pct, input.rate_lock_cltv), 3),
    hcltv: fixedNumber(firstPresent(input.hcltv_ratio, input.rate_lock_hcltv), 3),
    estimatedValue: fixedNumber(firstPresent(
      input.rate_lock_property_estimated_value,
      input.property_value,
      input.trans_details_purchase_price
    ), 2),
    appraisedValue: fixedNumber(firstPresent(
      input.subject_property_appraised_value,
      input.rate_lock_property_appraised_value
    ), 2),
    propertyType: canonicalPropertyType(input.property_type, input.is_non_warrantable_project),
    projectClassification: keyValue(input.project_classification),
    nonWarrantableProject: booleanValue(input.is_non_warrantable_project),
    occupancy: canonicalOccupancy(input.occupancy_status),
    propertyState: canonicalState(input.subject_property_state),
    propertyCounty: keyValue(input.property_county),
    propertyZip: canonicalZip(input.property_zip),
    propertyUnits: canonicalUnits(input.property_units),
    documentationType: keyValue(input.rate_lock_documentation_type),
    prepaymentPenalty: keyValue(input.rate_lock_prepay_penalty),
    impoundWaived: booleanValue(input.rate_lock_impound_waived),
    selfEmployed: booleanValue(firstPresent(
      input.rate_lock_self_employed_indicator,
      input.self_employed_indicator
    )),
    firstTimeHomebuyer: booleanValue(input.rate_lock_first_time_homebuyer),
    lenderPaidMi: booleanValue(input.rate_lock_lender_paid_mi),
    miPremiumPercent: fixedNumber(input.rate_lock_mi_premium_percent, 3),
    fundingAmount: fixedNumber(input.rate_lock_funding_amount, 2),
    mipPaidInCash: fixedNumber(input.rate_lock_mip_paid_in_cash, 2),
    interestOnlyTermMonths: integerValue(input.interest_only_term_months),
    noteRate: fixedNumber(input.trans_details_interest_rate, 3),
    ausType: keyValue(input.aus_type),
    ausRecommendation: keyValue(input.aus_recommendation),
    ausKey: keyValue(input.aus_key),
    dscrRatio: fixedNumber(input.dscr_ratio, 3)
  });
}

async function sha256(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export async function buildPickupCanonicalHashes(
  input: PickupCanonicalLoanInput
): Promise<PickupCanonicalHashes> {
  return {
    lockHash: await sha256(canonicalPickupLockIdentity(input)),
    pricingHash: await sha256(canonicalPickupPricingInputs(input)),
    lockDurationDays: integerValue(firstPresent(
      input.rate_lock_buy_side_number_of_days,
      input.rate_lock_request_number_of_days
    ))
  };
}

export async function buildPickupCanonicalHashesIfComplete(
  input: PickupCanonicalLoanInput
): Promise<PickupCanonicalHashes | null> {
  return hasPickupCanonicalEncompassInputs(input)
    ? buildPickupCanonicalHashes(input)
    : null;
}

export function pickupCanonicalStateChanged(input: {
  lockHash: string;
  pricingHash: string;
  activeSelectionId?: string | null;
  verifiedLockHash?: string | null;
  verifiedLockHashVersion?: number | null;
  verifiedPricingHash?: string | null;
  verifiedPricingHashVersion?: number | null;
  verifiedActiveSelectionId?: string | null;
}): boolean {
  return input.verifiedLockHashVersion !== PICKUP_LOCK_HASH_VERSION
    || input.verifiedPricingHashVersion !== PICKUP_PRICING_HASH_VERSION
    || input.verifiedLockHash !== input.lockHash
    || input.verifiedPricingHash !== input.pricingHash
    || (input.verifiedActiveSelectionId ?? null) !== (input.activeSelectionId ?? null);
}

function valuesEqual(expected: unknown, actual: unknown): boolean {
  return expected === null || expected === actual;
}

function numericValuesEqual(expected: unknown, actual: unknown, places: number): boolean {
  const expectedNumber = fixedNumber(expected, places);
  if (expectedNumber === null) {
    return true;
  }
  return expectedNumber === fixedNumber(actual, places);
}

function storedDecimalPrecision(value: unknown): number {
  const normalized = textValue(value)?.replace(/,/g, '');
  const fraction = normalized?.match(/^[+-]?\d+\.(\d+)$/)?.[1]
    ?.replace(/0+$/g, '');
  return Math.max(1, fraction?.length ?? 0);
}

function numericValuesEqualAtCoarserPrecision(expected: unknown, actual: unknown): boolean {
  const expectedNumber = finiteNumber(expected);
  if (expectedNumber === null) {
    return true;
  }
  const actualNumber = finiteNumber(actual);
  if (actualNumber === null) {
    return false;
  }
  const coarserPrecision = Math.min(
    storedDecimalPrecision(expected),
    storedDecimalPrecision(actual)
  );
  const tolerance = 0.5 * (10 ** -coarserPrecision);
  const floatingPointSlack = Number.EPSILON
    * Math.max(1, Math.abs(expectedNumber), Math.abs(actualNumber))
    * 4;
  return Math.abs(expectedNumber - actualNumber) <= tolerance + floatingPointSlack;
}

function rawValueIsMissing(value: unknown): boolean {
  return value === null
    || value === undefined
    || value === ''
    || (Array.isArray(value) && value.length === 0);
}

function rawValuesIdentical(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function addFieldComparison(
  comparisons: PickupFieldComparison[],
  field: PickupComparisonField,
  tier: PickupMismatchTier,
  encompassRaw: unknown,
  obRaw: unknown,
  matches: boolean,
  options: {
    forceNormalizedMatch?: boolean;
    obAbsenceIsDrift?: boolean;
    note?: string;
  } = {}
): void {
  const verdict: PickupFieldVerdict = !matches
    ? (!options.obAbsenceIsDrift
      && !rawValueIsMissing(encompassRaw)
      && rawValueIsMissing(obRaw)
      ? 'missing_in_ob'
      : 'drift')
    : options.forceNormalizedMatch
      || (!rawValueIsMissing(encompassRaw)
      && !rawValueIsMissing(obRaw)
      && !rawValuesIdentical(encompassRaw, obRaw))
      ? 'normalized_match'
      : 'match';
  comparisons.push({
    field,
    label: PICKUP_COMPARISON_FIELD_LABELS[field],
    tier,
    encompassRaw,
    obRaw,
    verdict,
    ...(options.note ? { note: options.note } : {})
  });
}

export function buildPickupComparisonDetails(
  encompass: PickupCanonicalLoanInput,
  ob: PickupObComparisonInput
): PickupFieldComparison[] {
  const comparisons: PickupFieldComparison[] = [];
  const hasInvestorCommitment = textValue(encompass.rate_lock_investor_commitment) !== null;
  const expectedInvestorRaw = hasInvestorCommitment
    ? encompass.investor_name
    : parseInvestorFromProgram(encompass.rate_lock_loan_program) ?? encompass.investor_name;
  const expectedInvestor = normalizePickupReconciliationInvestor(expectedInvestorRaw);
  const expectedProductRaw = encompass.rate_lock_loan_program;
  const expectedProduct = normalizePickupReconciliationProduct(encompass.rate_lock_loan_program);

  const encompassLockRate = pickupLockRateValue(encompass);
  addFieldComparison(comparisons, 'locked_rate', 'blocking',
    encompassLockRate, ob.lockedRate,
    numericValuesEqual(encompassLockRate, ob.lockedRate, 3));
  addFieldComparison(comparisons, 'locked_price', 'blocking',
    encompass.rate_lock_total_buy_price, ob.lockedPrice,
    numericValuesEqual(encompass.rate_lock_total_buy_price, ob.lockedPrice, 3));
  addFieldComparison(comparisons, 'investor', 'blocking', expectedInvestorRaw, ob.investor,
    expectedInvestor !== null
      && expectedInvestor === normalizePickupReconciliationInvestor(ob.investor));
  addFieldComparison(comparisons, 'product', 'blocking', expectedProductRaw, ob.product,
    expectedProduct !== null
      && expectedProduct === normalizePickupReconciliationProduct(ob.product));
  addFieldComparison(comparisons, 'lock_expiration_date', 'blocking',
    encompass.rate_lock_buy_side_lock_expires, ob.lockExpirationAt,
    dateOnly(encompass.rate_lock_buy_side_lock_expires) !== null
      && dateOnly(encompass.rate_lock_buy_side_lock_expires) === pickupObBusinessDate(ob.lockExpirationAt));

  const expectedBaseLoanAmount = firstPresent(
    encompass.rate_lock_base_loan_amount,
    encompass.trans_details_loan_amt
  );
  const expectedLtv = finiteNumber(firstPresent(
    encompass.freddie_mac_ltv_ratio_pct,
    encompass.rate_lock_ltv
  ));
  const secondLien = canonicalLien(firstPresent(
    encompass.lien_position,
    ob.lienPosition
  )) === 'second';
  const actualLtvRaw = secondLien ? ob.secondLienSubjectRatio : ob.ltv;
  const actualCltvDisplay = secondLien ? ob.secondLienCombinedRatio : ob.cltv;
  const actualHcltvDisplay = secondLien ? ob.secondLienCombinedRatio : ob.hcltv;
  const actualLtv = finiteNumber(actualLtvRaw);
  const actualCltvRaw = finiteNumber(ob.cltv);
  const actualCltv = secondLien
    ? finiteNumber(ob.secondLienCombinedRatio)
    : actualCltvRaw === 0 ? actualLtv : actualCltvRaw;
  const actualHcltvRaw = finiteNumber(ob.hcltv);
  const actualHcltv = secondLien
    ? finiteNumber(ob.secondLienCombinedRatio)
    : actualHcltvRaw === 0 ? actualCltv : actualHcltvRaw;

  addFieldComparison(comparisons, 'base_loan_amount', 'advisory',
    expectedBaseLoanAmount, ob.baseLoanAmount,
    numericValuesEqual(expectedBaseLoanAmount, ob.baseLoanAmount, 2));
  addFieldComparison(comparisons, 'loan_purpose', 'advisory',
    encompass.trans_details_loan_purpose, ob.loanPurpose, valuesEqual(
      canonicalPurpose(encompass.trans_details_loan_purpose),
      canonicalPurpose(ob.loanPurpose)
    ));
  const expectedLoanType = canonicalLoanType(encompass.trans_details_loan_type);
  addFieldComparison(comparisons, 'loan_type', 'advisory',
    encompass.trans_details_loan_type, ob.loanType,
    expectedLoanType === null || loanTypesEqual(expectedLoanType, canonicalLoanType(ob.loanType)));
  addFieldComparison(comparisons, 'loan_term_months', 'advisory',
    encompass.loan_term_months, ob.loanTermMonths, valuesEqual(
      canonicalLoanTerm(encompass.loan_term_months),
      canonicalLoanTerm(ob.loanTermMonths)
    ));
  addFieldComparison(comparisons, 'amortization_type', 'advisory',
    encompass.amortization_type, ob.amortizationType, valuesEqual(
      canonicalAmortization(encompass.amortization_type),
      canonicalAmortization(ob.amortizationType)
    ));
  addFieldComparison(comparisons, 'lien_position', 'advisory',
    encompass.lien_position, ob.lienPosition, valuesEqual(
      canonicalLien(encompass.lien_position),
      canonicalLien(ob.lienPosition)
    ));
  addFieldComparison(comparisons, 'representative_fico', 'advisory',
    encompass.cx_loan_level_fico, ob.representativeFico, valuesEqual(
    integerValue(encompass.cx_loan_level_fico),
    integerValue(ob.representativeFico)
  ));
  const loanLevelFico = integerValue(encompass.cx_loan_level_fico);
  const borrowerScores = [
    integerValue(encompass.borrower_middle_score),
    integerValue(encompass.coborrower_middle_score)
  ].filter((score): score is number => score !== null);
  const lowestBorrowerMiddleScore = borrowerScores.length > 0
    ? Math.min(...borrowerScores)
    : null;
  if (loanLevelFico !== null
    && lowestBorrowerMiddleScore !== null
    && loanLevelFico !== lowestBorrowerMiddleScore) {
    addFieldComparison(comparisons, 'fico_internal_consistency', 'advisory',
      encompass.cx_loan_level_fico, lowestBorrowerMiddleScore, false, {
        note: 'Loan-level FICO differs from the lowest borrower middle score'
      });
  }
  const dtiMatches = numericValuesEqual(encompass.dti_ratio, ob.dti, 2);
  const roundedEncompassDti = finiteNumber(fixedNumber(encompass.dti_ratio, 2));
  const roundedObDti = finiteNumber(fixedNumber(ob.dti, 2));
  const dtiTier: PickupMismatchTier = !dtiMatches
    && roundedEncompassDti !== null
    && roundedObDti !== null
    && roundedEncompassDti > roundedObDti
    ? 'blocking'
    : 'advisory';
  addFieldComparison(comparisons, 'dti', dtiTier, encompass.dti_ratio, ob.dti,
    dtiMatches, {
      note: dtiTier === 'blocking'
        ? 'Optimal Blue priced a lower DTI'
        : undefined
    });
  const expectedLtvRaw = firstPresent(encompass.freddie_mac_ltv_ratio_pct, encompass.rate_lock_ltv);
  addFieldComparison(comparisons, 'ltv', 'advisory', expectedLtvRaw, actualLtvRaw,
    numericValuesEqualAtCoarserPrecision(expectedLtv, actualLtv), {
      forceNormalizedMatch: secondLien
        && !rawValueIsMissing(expectedLtvRaw)
        && !rawValueIsMissing(actualLtvRaw)
    });
  const expectedCltvRaw = firstPresent(encompass.trans_details_cltv_ratio_pct, encompass.rate_lock_cltv);
  addFieldComparison(comparisons, 'cltv', 'advisory', expectedCltvRaw, actualCltvDisplay,
    numericValuesEqualAtCoarserPrecision(expectedCltvRaw, actualCltv), {
      forceNormalizedMatch: secondLien
        && !rawValueIsMissing(expectedCltvRaw)
        && !rawValueIsMissing(actualCltvDisplay)
    });
  const expectedHcltvRaw = firstPresent(encompass.hcltv_ratio, encompass.rate_lock_hcltv);
  addFieldComparison(comparisons, 'hcltv', 'advisory', expectedHcltvRaw, actualHcltvDisplay,
    numericValuesEqualAtCoarserPrecision(expectedHcltvRaw, actualHcltv), {
      forceNormalizedMatch: secondLien
        && !rawValueIsMissing(expectedHcltvRaw)
        && !rawValueIsMissing(actualHcltvDisplay)
    });
  const ltvArithmetic = calculatedLoanCenterLtv(encompass);
  if (ltvArithmetic && ltvArithmetic.difference > 0.5) {
    addFieldComparison(comparisons, 'ltv_arithmetic', 'advisory',
      ltvArithmetic.reportedLtv, ltvArithmetic.calculatedLtv, false, {
        note: ltvArithmetic.difference > 2
          ? `Loan Center LTV arithmetic differs by ${ltvArithmetic.difference.toFixed(2)} points — review the loan amount and denominator`
          : `Loan Center LTV arithmetic differs by ${ltvArithmetic.difference.toFixed(2)} points`
      });
  }
  const expectedEstimatedValue = firstPresent(
    encompass.rate_lock_property_estimated_value,
    encompass.property_value,
    encompass.trans_details_purchase_price
  );
  addFieldComparison(comparisons, 'estimated_value', 'advisory',
    expectedEstimatedValue, ob.estimatedValue,
    numericValuesEqual(expectedEstimatedValue, ob.estimatedValue, 2));
  const expectedAppraisedValue = firstPresent(
    encompass.rate_lock_property_appraised_value,
    encompass.subject_property_appraised_value
  );
  addFieldComparison(comparisons, 'appraised_value', 'advisory',
    expectedAppraisedValue, ob.appraisedValue,
    numericValuesEqual(expectedAppraisedValue, ob.appraisedValue, 2));
  addFieldComparison(comparisons, 'property_type', 'advisory',
    encompass.property_type, ob.propertyType, valuesEqual(
      canonicalPropertyType(encompass.property_type, encompass.is_non_warrantable_project),
      canonicalPropertyType(ob.propertyType, ob.nonWarrantableProject)
    ));
  addFieldComparison(comparisons, 'project_classification', 'advisory',
    encompass.project_classification, ob.projectClassification,
    rawValueIsMissing(ob.projectClassification)
      ? projectClassificationIsBenign(encompass.project_classification)
      : valuesEqual(
        keyValue(encompass.project_classification),
        keyValue(ob.projectClassification)
      ),
    {
      forceNormalizedMatch: rawValueIsMissing(ob.projectClassification)
        && projectClassificationIsBenign(encompass.project_classification),
      obAbsenceIsDrift: true
    });
  const derivedObNonWarrantableProject = rawValueIsMissing(ob.nonWarrantableProject)
    && keyValue(ob.propertyType) === 'nonwarrantablecondo'
    ? true
    : ob.nonWarrantableProject;
  addFieldComparison(comparisons, 'non_warrantable_project', 'advisory',
    encompass.is_non_warrantable_project, derivedObNonWarrantableProject,
    rawValueIsMissing(derivedObNonWarrantableProject)
      ? nonWarrantableProjectIsBenign(encompass.is_non_warrantable_project)
      : valuesEqual(
        booleanValue(encompass.is_non_warrantable_project),
        booleanValue(derivedObNonWarrantableProject)
      ),
    {
      forceNormalizedMatch: rawValueIsMissing(ob.nonWarrantableProject)
        && (derivedObNonWarrantableProject === true
          || nonWarrantableProjectIsBenign(encompass.is_non_warrantable_project)),
      obAbsenceIsDrift: true
    });
  addFieldComparison(comparisons, 'occupancy', 'blocking',
    encompass.occupancy_status, ob.occupancy, valuesEqual(
      canonicalOccupancy(encompass.occupancy_status),
      canonicalOccupancy(ob.occupancy)
    ));
  addFieldComparison(comparisons, 'property_state', 'advisory',
    encompass.subject_property_state, ob.propertyState, valuesEqual(
      canonicalState(encompass.subject_property_state),
      canonicalState(ob.propertyState)
    ));
  addFieldComparison(comparisons, 'property_county', 'advisory',
    encompass.property_county, ob.propertyCounty, valuesEqual(
      keyValue(encompass.property_county),
      keyValue(ob.propertyCounty)
    ));
  addFieldComparison(comparisons, 'property_zip', 'advisory',
    encompass.property_zip, ob.propertyZip, valuesEqual(
      canonicalZip(encompass.property_zip),
      canonicalZip(ob.propertyZip)
    ));
  addFieldComparison(comparisons, 'property_units', 'advisory',
    encompass.property_units, ob.propertyUnits, valuesEqual(
      canonicalUnits(encompass.property_units),
      canonicalUnits(ob.propertyUnits)
    ));
  const expectedDocumentationType = firstPresent(
    encompass.cx_doc_type,
    encompass.rate_lock_documentation_type
  );
  const documentationScope = ob.documentationScope
    ?? (textValue(ob.product) ? (
      hasExpandedDocumentationScope(undefined, ob.product) ? 'expanded' : 'standard'
    ) : undefined);
  const documentationBasis = documentationScope === 'standard'
    ? 'FullDoc'
    : ob.documentationType;
  const documentationRaw = documentationScope === 'standard'
    ? 'Full Doc (Standard product)'
    : ob.documentationType;
  const ignoredDocumentationValue = documentationScope === 'standard'
    ? textValue(ob.documentationApiValue)
    : null;
  // Agency streamline refinances price as Standard products, whose OB basis is
  // FullDoc. The exception stays scoped: an Expanded Guidelines product's
  // explicit FullDoc is still a different documentation type.
  const streamlineOnStandardProduct = documentationScope === 'standard'
    && canonicalDocumentationType(expectedDocumentationType) === 'streamline';
  addFieldComparison(comparisons, 'documentation_type', 'blocking',
    expectedDocumentationType, documentationRaw,
    documentationTypesEqual(
      expectedDocumentationType,
      documentationBasis
    ) || streamlineOnStandardProduct, {
      note: ignoredDocumentationValue
        ? `API carries ${ignoredDocumentationValue} — not read by this product`
        : undefined
    });

  const lockFormPrepayTerm = canonicalPrepaymentTerm(
    encompass.rate_lock_prepay_term_months
  );
  const loanLevelPrepayTerm = canonicalPrepaymentTerm(encompass.prepay_term_months);
  const hasLockFormPrepayTerm = lockFormPrepayTerm !== null;
  const hasLoanLevelPrepayTerm = loanLevelPrepayTerm !== null;
  const hasPrepayTermEvidence = hasLockFormPrepayTerm || hasLoanLevelPrepayTerm;
  const prepayTermsInternallyConsistent = !hasLockFormPrepayTerm
    || !hasLoanLevelPrepayTerm
    || lockFormPrepayTerm === loanLevelPrepayTerm;
  const expectedPrepayTerm = lockFormPrepayTerm ?? loanLevelPrepayTerm;
  const prepayTermRaw = {
    lockFormTermMonths: encompass.rate_lock_prepay_term_months ?? null,
    loanLevelTermMonths: encompass.prepay_term_months ?? null
  };
  const prepayMatches = hasPrepayTermEvidence
    ? prepayTermsInternallyConsistent
      && expectedPrepayTerm === canonicalPrepaymentTerm(ob.prepaymentPenalty)
    : valuesEqual(
      prepaymentPenaltyPresent(encompass.rate_lock_prepay_penalty),
      prepaymentPenaltyPresent(ob.prepaymentPenalty)
    );
  addFieldComparison(comparisons, 'prepayment_penalty', 'blocking',
    hasPrepayTermEvidence ? prepayTermRaw : encompass.rate_lock_prepay_penalty,
    ob.prepaymentPenalty,
    prepayMatches,
    {
      note: prepayTermsInternallyConsistent
        ? undefined
        : 'Encompass internally inconsistent'
    });
  const requestPrepayFlag = booleanValue(encompass.rate_lock_prepay_penalty);
  if (hasPrepayTermEvidence && requestPrepayFlag !== null) {
    const termSaysPenalty = expectedPrepayTerm !== '0';
    const requestFlagMatchesTerm = prepayTermsInternallyConsistent
      && requestPrepayFlag === termSaysPenalty;
    addFieldComparison(comparisons, 'prepayment_penalty_flag', 'advisory',
      encompass.rate_lock_prepay_penalty,
      prepayTermRaw,
      requestFlagMatchesTerm,
      {
        forceNormalizedMatch: requestFlagMatchesTerm,
        note: requestFlagMatchesTerm
          ? undefined
          : 'Lock request flag disagrees with penalty term'
      });
  }
  addFieldComparison(comparisons, 'impound_waived', 'advisory',
    encompass.rate_lock_impound_waived, ob.impoundWaived, valuesEqual(
      booleanValue(encompass.rate_lock_impound_waived),
      booleanValue(ob.impoundWaived)
    ));
  const selfEmploymentInputs = [
    encompass.rate_lock_self_employed_indicator,
    encompass.self_employed_indicator
  ];
  const expectedSelfEmployed = firstPresent(...selfEmploymentInputs);
  const expectedSelfEmployedRaw = expectedSelfEmployed === undefined
    ? firstDefined(...selfEmploymentInputs)
    : expectedSelfEmployed;
  addFieldComparison(comparisons, 'self_employed', 'advisory',
    expectedSelfEmployedRaw, ob.selfEmployed, valuesEqual(
      booleanValue(expectedSelfEmployed),
      booleanValue(ob.selfEmployed)
    ), {
      forceNormalizedMatch: rawValueIsMissing(expectedSelfEmployedRaw)
    });
  addFieldComparison(comparisons, 'first_time_homebuyer', 'advisory',
    encompass.rate_lock_first_time_homebuyer, ob.firstTimeHomebuyer, valuesEqual(
      booleanValue(encompass.rate_lock_first_time_homebuyer),
      booleanValue(ob.firstTimeHomebuyer)
    ), {
      forceNormalizedMatch: rawValueIsMissing(encompass.rate_lock_first_time_homebuyer)
    });
  addFieldComparison(comparisons, 'lender_paid_mi', 'advisory',
    encompass.rate_lock_lender_paid_mi, ob.lenderPaidMi, valuesEqual(
      booleanValue(encompass.rate_lock_lender_paid_mi),
      booleanValue(ob.lenderPaidMi)
    ));
  addFieldComparison(comparisons, 'mi_premium_percent', 'advisory',
    encompass.rate_lock_mi_premium_percent, ob.miPremiumPercent,
    numericValuesEqual(encompass.rate_lock_mi_premium_percent, ob.miPremiumPercent, 3)
      || (fixedNumber(encompass.rate_lock_mi_premium_percent, 3) === '0.000'
        && rawValueIsMissing(ob.miPremiumPercent)), {
      forceNormalizedMatch: fixedNumber(encompass.rate_lock_mi_premium_percent, 3) === '0.000'
        && rawValueIsMissing(ob.miPremiumPercent)
    });
  addFieldComparison(comparisons, 'funding_amount', 'advisory',
    encompass.rate_lock_funding_amount, ob.fundingAmount,
    numericValuesEqual(encompass.rate_lock_funding_amount, ob.fundingAmount, 2));
  addFieldComparison(comparisons, 'mip_paid_in_cash', 'advisory',
    encompass.rate_lock_mip_paid_in_cash, ob.mipPaidInCash,
    numericValuesEqual(encompass.rate_lock_mip_paid_in_cash, ob.mipPaidInCash, 2));
  addFieldComparison(comparisons, 'interest_only_term_months', 'advisory',
    encompass.interest_only_term_months, ob.interestOnlyTermMonths, valuesEqual(
      interestOnlyPresent(encompass.interest_only_term_months),
      interestOnlyPresent(ob.interestOnlyTermMonths)
    ));
  const noteRate = finiteNumber(encompass.trans_details_interest_rate);
  const lockRate = finiteNumber(encompassLockRate);
  if (noteRate !== null
    && lockRate !== null
    && !numericValuesEqual(noteRate, lockRate, 3)) {
    addFieldComparison(comparisons, 'note_rate', 'blocking',
      encompass.trans_details_interest_rate, encompassLockRate, false, {
        note: 'Loan Center note rate differs from the lock-form rate'
      });
  }
  const declaredAus = canonicalAusType(encompass.aus_type);
  const ausRecommendation = canonicalAusRecommendation(encompass.aus_recommendation);
  const ausKey = textValue(encompass.aus_key);
  const ausIntegrityIssues: string[] = [];
  if (declaredAus === 'manualunderwriting' && (ausRecommendation !== null || ausKey !== null)) {
    ausIntegrityIssues.push('Manual underwriting requires a blank recommendation and case ID');
  }
  if (ausRecommendation === 'accept') {
    if (!ausKey) {
      ausIntegrityIssues.push('Accept requires an AUS case ID');
    } else if (!ausKey.toUpperCase().startsWith('A')) {
      ausIntegrityIssues.push('Accept requires an AUS case ID beginning with A');
    }
  }
  if (ausRecommendation === 'approveeligible' && !ausKey) {
    ausIntegrityIssues.push('Approve/Eligible requires an AUS case ID');
  }
  if (ausIntegrityIssues.length > 0) {
    addFieldComparison(comparisons, 'aus_integrity', 'blocking', {
      declaredAus: encompass.aus_type ?? null,
      recommendation: encompass.aus_recommendation ?? null,
      caseId: encompass.aus_key ?? null
    }, 'Internally consistent', false, {
      note: `Encompass internally inconsistent — ${ausIntegrityIssues.join('; ')}`
    });
  }
  const trueAus = derivedAusType(encompass.aus_recommendation, encompass.aus_type);
  addFieldComparison(comparisons, 'aus_type', 'blocking',
    trueAus, ob.ausType, valuesEqual(
      trueAus,
      canonicalAusType(ob.ausType)
    ), {
      note: ausRecommendation
        ? `Derived from Encompass recommendation ${textValue(encompass.aus_recommendation)}`
        : undefined
    });
  addFieldComparison(comparisons, 'dscr_ratio', 'advisory',
    encompass.dscr_ratio, ob.dscrRatio,
    numericValuesEqual(encompass.dscr_ratio, ob.dscrRatio, 2));

  return comparisons;
}

export function comparePickupCanonicalToOb(
  encompass: PickupCanonicalLoanInput,
  ob: PickupObComparisonInput
): PickupVerificationMismatch[] {
  return buildPickupComparisonDetails(encompass, ob)
    .filter((comparison) => (
      comparison.verdict === 'drift' || comparison.verdict === 'missing_in_ob'
    ))
    .map(({ field, tier }) => ({ field, tier }));
}
